import React from 'react';
import classNames from 'classnames';
import AlertBox, { Heading } from 'components/shared/AlertBox';
import { Button, ButtonGroup, Spinner } from 'ui';
import { parseHippoEvent } from 'components/shared/hippo';
import RecordingEmbed from 'components/Practice/shared/RecordingEmbed';
import ErrorModal from 'components/Practice/shared/ErrorModal';
import InfoTooltip from 'components/shared/InfoTooltip';
import recordingPermissionErrorTypes from 'constants/recordingPermissionErrorTypes.js';
import { isMobileDevice, isSafari, isIE, isChrome } from 'utils/browserCheck';
import trackEvent from 'misc/trackEvent';

export default class RecordingQuestion extends React.Component {
  state = {
    recording: false,
    loading: true,
    isComplete: false,
    hippoToken: null,
    errorType: null,
    offlineError: false,
    videoUploaded: false,
    tryAgainModalShown: false,
    importTimingStarted: null,
  };

  componentDidMount() {
    window.hippoWidget.onLoad(() => {
      this.setState({ loading: false });
    });
  }

  saveAttempt = hippoToken => {
    this.props.handleSave(this.state.hippoToken);
  };

  toggleTryAgainModal = () => {
    this.setState(state => ({
      tryAgainModalShown: !state.tryAgainModalShown,
    }));
  };

  tryAgain = () => {
    if (navigator && navigator.onLine) {
      trackEvent('practice/recording_try_again_clicked', {
        content_type: 'on_demand_practice_scenario',
        content_id: this.props.questionId,
      });
      this.props.resetAnswer();
      this.setState({
        hippoToken: null,
        isComplete: false,
        tryAgainModalShown: false,
      });
    } else {
      this.setState({ offlineError: true });
    }
  };

  sourceUploaded = isUpload => () => {
    this.trackSourceUploaded(isUpload);
    if (this.state.hippoToken) {
      if (this.props.setAnswer) {
        this.props.setAnswer(this.state.hippoToken, 'hippo_video');
      }
      this.setState({
        isComplete: true,
      });
    }
    this.setState({
      videoUploaded: true,
    });
  };

  videoSubmitted = isUpload => event => {
    this.trackVideoSubmitted(isUpload);
    const hippoToken = parseHippoEvent(event);
    this.setState(
      {
        hippoToken,
        recording: false,
      },
      () => {
        if (this.state.videoUploaded) {
          if (this.props.setAnswer) {
            this.props.setAnswer(this.state.hippoToken, 'hippo_video');
          }
          this.setState({
            isComplete: true,
          });
        }
      },
    );
  };

  videoCancelled = isUpload => data => {
    this.trackVideoCancelled(isUpload);
    this.reloadRecording(data);
  };

  importProgress = properties => {
    if (properties.operation === 'started') {
      this.trackImportStarted();
    }
  };

  importComplete = data => {
    this.trackImportComplete(data);
    this.trackRecordingData(data);
  };

  recordInitiated = data => {
    this.trackRecordingInitiated(data);
    this.trackRecordingData(data);
  };

  recordStarted = () => {
    this.trackRecordingStarted();
  };

  recordAborted = () => {
    this.trackRecordingAborted();
    this.reloadRecording();
  };

  recordStopped = () => {
    this.trackRecordingStopped();
  };

  handleRecord = () => {
    const { recordingConfig } = this.props;
    if (this.props.isPreviewing) {
      return;
    }
    this.setState({ recording: true });
    window.hippoWidget.on('record_error', this.showErrorModal);
    window.hippoWidget.startRecording(
      recordingConfig,
      this.props.currentUserId,
    );
    window.hippoWidget.on('record_initiated', this.recordInitiated);
    window.hippoWidget.on('record_started', this.recordStarted);
    window.hippoWidget.on('record_aborted', this.recordAborted);
    window.hippoWidget.on('record_stopped', this.recordStopped);
    window.hippoWidget.on('video_submitted', this.videoSubmitted(false));
    window.hippoWidget.on('source_uploaded', this.sourceUploaded(false));
    window.hippoWidget.on('video_cancelled', this.videoCancelled(false));
  };

  handleUpload = () => {
    window.hippoWidget.on('import_failed', this.handleUploadError);
    window.hippoWidget.import(this.props.currentUserId);
    window.hippoWidget.on('import_progress', properties => {
      if (properties.operation === 'started') {
        this.trackImportStarted();
      }
    });
    window.hippoWidget.on('video_submitted', this.videoSubmitted(true));
    window.hippoWidget.on('import_complete', this.importComplete);
    window.hippoWidget.on('source_uploaded', this.sourceUploaded(true));
    window.hippoWidget.on('video_cancelled', this.videoCancelled(true));
    window.hippoWidget.on('record_aborted', this.reloadRecording);
  };

  reloadRecording = () => {
    this.setState({
      hippoToken: null,
      recording: false,
      isComplete: false,
    });
  };

  handleUploadError = error => {
    if (error.reason === 'invalid_file_format') {
      this.setState({
        errorType: error.reason + '_video',
      });
    } else if (error.reason === 'size_limit_exceeded') {
      this.setState({
        errorType: error.reason,
      });
    } else if (error.reason === 'processing_error') {
      if (error.error.includes('Media length is too short')) {
        this.setState({
          errorType: 'video_too_small',
        });
      } else if (
        error.error.includes('Video Codec Unidentified') ||
        error.error.includes('Audio Codec Unidentified')
      ) {
        this.setState({
          errorType: 'unidentified_codec',
        });
      }
    }
    this.trackImportFailure(error);
    this.reloadRecording();
  };

  showErrorModal = response => {
    const errorType = response.error;

    if (Object.values(recordingPermissionErrorTypes).includes(errorType)) {
      this.trackPermissionDenied(errorType);
    } else {
      this.trackRecordingError(errorType);
    }

    this.setState({
      errorType: null,
      hippoToken: null,
      recording: false,
      isComplete: false,
    });
    if (
      errorType !==
      recordingPermissionErrorTypes.HIPPO_SCREEN_PERMISSION_DENIED_ERROR
    ) {
      this.setState({ errorType });
    }
  };

  answerUrl = () =>
    this.props.hippoPreviewBase + this.state.hippoToken + '?type=lessonly';

  handleResetError = () => {
    this.setState({
      errorType: null,
      hippoToken: null,
      recording: false,
      isComplete: false,
    });
  };

  closeOfflineErrorModal = () => {
    this.setState({
      offlineError: false,
    });
  };

  recordingSupported = () => {
    if (isMobileDevice()) {
      return false;
    }
    if (this.isScreenRecording() && !isChrome()) {
      return false;
    }
    if (isSafari() || isIE()) {
      return false;
    }
    return true;
  };

  isScreenRecording = () => this.props.recordingConfig.screenRecord;

  getRecordButtonText = () => {
    if (this.isScreenRecording()) {
      return t('questions.question.hippo_screen_recording.record');
    }
    return t('questions.question.audio_recording.record');
  };

  getUploadButtonText = () => {
    if (this.isScreenRecording()) {
      return t('questions.question.hippo_screen_recording.upload');
    }
    return t('questions.question.audio_recording.upload');
  };

  /* Start Tracking events */

  trackingEventParameters = () => {
    const { questionId } = this.props;
    return {
      scenario_id: questionId,
    };
  };

  trackRecordingInitiated(data) {
    const eventData = {
      ...this.trackingEventParameters(),
      token: data.token,
    };
    trackEvent('on_demand_practice/recording_initiated', eventData);
  }

  trackRecordingData(data) {
    const eventData = { asset_id: data.asset_id, token: data.token };
    trackEvent('recording_metadata', eventData);
  }

  trackRecordingStarted = () => {
    const eventData = {
      ...this.trackingEventParameters(),
    };
    trackEvent('on_demand_practice/recording_started', eventData);
  };

  trackRecordingStopped = () => {
    const eventData = {
      ...this.trackingEventParameters(),
    };
    trackEvent('on_demand_practice/recording_stopped', eventData);
  };

  trackRecordingAborted = () => {
    const eventData = {
      ...this.trackingEventParameters(),
    };
    trackEvent('on_demand_practice/recording_aborted', eventData);
  };

  trackImportStarted = () => {
    this.setState({ importTimingStarted: performance.now() });
    trackEvent('on_demand_practice/upload_started', {
      scenario_id: this.props.questionId,
    });
  };

  trackImportComplete = data => {
    const { importTimingStarted } = this.state;
    const importTimingComplete = performance.now();
    const importTimingFinal = importTimingComplete - importTimingStarted;
    trackEvent('on_demand_practice/upload_complete', {
      scenario_id: this.props.questionId,
      token: data.token,
      duration: data.media_duration,
      total_import_time: importTimingFinal,
    });
  };

  trackImportFailure = error => {
    trackEvent('on_demand_practice/upload_failed', {
      scenario_id: this.props.questionId,
      error,
    });
  };

  trackSourceUploaded = isUpload => {
    const eventData = {
      ...this.trackingEventParameters(),
      upload: isUpload,
    };
    trackEvent('on_demand_practice/source_uploaded', eventData);
  };

  trackVideoSubmitted = isUpload => {
    const eventData = {
      ...this.trackingEventParameters(),
      upload: isUpload,
    };
    trackEvent('on_demand_practice/video_submitted', eventData);
  };

  trackVideoCancelled = isUpload => {
    const eventData = {
      ...this.trackingEventParameters(),
      upload: isUpload,
    };
    trackEvent('on_demand_practice/video_cancelled', eventData);
  };

  trackPermissionDenied = errorType => {
    const eventData = {
      ...this.trackingEventParameters(),
      upload: false,
      error_type: errorType,
    };
    trackEvent('on_demand_practice/permission_denied', eventData);
  };

  trackRecordingError = errorType => {
    const eventData = {
      ...this.trackingEventParameters(),
      upload: false,
      error_type: errorType,
    };
    trackEvent('on_demand_practice/recording_error', eventData);
  };

  /* End Tracking Events */

  render() {
    const {
      isComplete,
      errorType,
      loading,
      tryAgainModalShown,
      offlineError,
      recording,
    } = this.state;
    const disabledMediaButtonClass = classNames({
      'QuestionElement--disableRecordingPreview': this.props.isPreviewing,
    });
    return (
      <>
        <div>
          {loading && (
            <div className="d-f h-150 jc-c ai-c">
              <Spinner />
            </div>
          )}
          {!loading && !recording && !isComplete && (
            <div className="QuestionElement-recordingActions">
              <div>
                <Button
                  tall
                  customColor
                  disabled={loading || !this.recordingSupported()}
                  onClick={this.handleRecord}
                  className={`${disabledMediaButtonClass}`}
                >
                  {this.getRecordButtonText()}
                </Button>
                {!this.recordingSupported() && (
                  <InfoTooltip
                    tooltip={t(
                      'questions.question.media_recording.unsupported',
                    )}
                    className="ml-8 tooltip-fixedWidth"
                  />
                )}
              </div>
              <button
                type="button"
                className={`u-linkBtn mt-8 fz-bigger ${disabledMediaButtonClass}`}
                onClick={this.handleUpload}
                disabled={this.props.isPreviewing}
              >
                {this.getUploadButtonText()}
              </button>
            </div>
          )}
          {recording && (
            <div className="QuestionElement-recordingActions">
              <Spinner size="large" />
            </div>
          )}
          {!loading && isComplete && !recording && (
            <>
              <div className="Video Video--playable">
                <RecordingEmbed embedUrl={this.answerUrl()} />
              </div>
              <ButtonGroup alignEnd className="RecordingQuestion-actions mt-32">
                <Button className="mr-16" onClick={this.toggleTryAgainModal}>
                  {t('common.try_again')}
                </Button>
              </ButtonGroup>
            </>
          )}
        </div>
        {errorType && (
          <ErrorModal errorType={errorType} onAction={this.handleResetError} />
        )}
        {offlineError && (
          <AlertBox onDismiss={this.closeOfflineErrorModal}>
            <Heading>
              {t('practice.on_demand_practice.recording.offline_error.title')}
            </Heading>
            {t(
              'practice.on_demand_practice.recording.offline_error.description',
            )}
            <ButtonGroup alignEnd className="mt-8">
              <Button customColor onClick={this.closeOfflineErrorModal}>
                {t('common.ok')}
              </Button>
            </ButtonGroup>
          </AlertBox>
        )}
        {tryAgainModalShown && (
          <AlertBox onDismiss={this.toggleTryAgainModal}>
            <Heading>{t('common.try_again')}</Heading>
            {t('practice.on_demand_practice.recording.try_again_note')}
            <ButtonGroup alignEnd className="mt-16">
              <Button onClick={this.toggleTryAgainModal}>
                {t('common.cancel')}
              </Button>
              <Button customColor onClick={this.tryAgain}>
                {t('common.try_again')}
              </Button>
            </ButtonGroup>
          </AlertBox>
        )}
      </>
    );
  }
}
